import React from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import {
  AI_ENDPOINTS,
  AUTOCOMPLETE_FIELDS,
  FormFieldsMap,
  RATE_ACTION
} from 'features/ai-generators/constants';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import queryString from 'query-string';
import strings from 'strings';
import { FPIconButton, TextBox } from 'UI/components/atoms';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { UIStatus } from 'UI/constants/status';
import { SvgMakeAi } from 'UI/res';

import { AdditionalInstructionsContainer, OptionsContainer } from './GeneratorOptions.styled';

export const GeneratorOptions = ({
  enabled,
  getRateButtonColor,
  hasGeneratedData,
  onClickGenerate,
  onClickThumbsDown,
  onClickThumbsUp,
  onCopyToClipboard,
  onSelectChange,
  onSubmit,
  optionsExtendedParams,
  submitButtonName
}) => {
  const { errors, watch } = useFormContext();
  const formValues = watch();

  const { state } = useFetchWithStatusV2({
    endpoint:
      Object.keys(optionsExtendedParams).length !== 0
        ? `${AI_ENDPOINTS.presentationOptions}?${queryString.stringify(optionsExtendedParams)}`
        : AI_ENDPOINTS.presentationOptions
  });

  const isFetchingPresentationOptions = state.status === UIStatus.Loading;
  const presentationOptions = state.status === UIStatus.Success ? state?.results : {};

  return (
    <form onSubmit={onSubmit}>
      <OptionsContainer>
        <Grid container spacing={2} alignItems="center" justify="space-between">
          {AUTOCOMPLETE_FIELDS.map(({ key, label, optionsKey, disableClearable, columns }) => (
            <Grid key={key} item xs={6} sm={columns.sm} md={columns.md}>
              <AutocompleteSelect
                name={key}
                label={label}
                defaultOptions={presentationOptions[optionsKey]}
                disableClearable={disableClearable}
                disabled={!enabled}
                error={!!errors[key]}
                errorText={errors[key]?.message}
                fetching={isFetchingPresentationOptions}
                getOptionLabel={option => option.title}
                getOptionSelected={(option, value) => option?.id === value?.id}
                onSelect={onSelectChange}
                selectedValue={formValues[key]}
              />
            </Grid>
          ))}

          <Grid item xs={6} sm={3} md={2} container wrap="nowrap">
            <FPIconButton
              onClick={onCopyToClipboard}
              showTooltipWhenDisabled
              disabled={!hasGeneratedData || !enabled}
              icon={FileCopyIcon}
              iconMode="custom"
              family="mini"
              tooltipProps={{ title: strings.shared.ui.copy }}
            />

            <FPIconButton
              onClick={onClickThumbsUp}
              showTooltipWhenDisabled
              iconProps={{
                color: getRateButtonColor(RATE_ACTION.ThumbsUp)
              }}
              disabled={!hasGeneratedData || !enabled}
              icon={ThumbUpIcon}
              iconMode="custom"
              family="mini"
              tooltipProps={{ title: strings.shared.ui.like }}
            />

            <FPIconButton
              onClick={onClickThumbsDown}
              showTooltipWhenDisabled
              iconProps={{
                color: getRateButtonColor(RATE_ACTION.ThumbsDown)
              }}
              iconMode="custom"
              disabled={!hasGeneratedData || !enabled}
              icon={ThumbDownIcon}
              family="mini"
              tooltipProps={{ title: strings.shared.ui.dislike }}
            />
          </Grid>

          <AdditionalInstructionsContainer>
            <TextBox
              name={FormFieldsMap.Instructions.key}
              disabled={!enabled}
              fullWidth
              label={strings.ai.generators.inputs.instructionsLabel}
              multiline
              noMargin
              onChange={onSelectChange}
              placeholder={strings.ai.generators.inputs.instructionsPlaceholder}
              rows={2}
              value={formValues[FormFieldsMap.Instructions.key] || ''}
            />

            <FPActionButton
              icon={SvgMakeAi}
              type="submit"
              disabled={!enabled || !onClickGenerate}
              color="primary"
              width="100%"
              text={submitButtonName || strings.ai.generators.actions.generateWithAI}
              variant="outlined"
              withGradient
            />
          </AdditionalInstructionsContainer>
        </Grid>
      </OptionsContainer>
    </form>
  );
};
